import classnames from "classnames"

import FilterIcon from "shared/components/icons/filter_icon"
import DelayIcon from "shared/components/icons/delay_icon"
import { verifyIntendedPromiseRejection } from "./utils/modal_promise"
import { cancellationError } from "./utils/error_map"

import styles from "./diy_tanda_selected.module.scss"
import { Service } from "./utils/interfaces"
import { MouseEventHandler } from "react"

interface Props {
  canEdit: boolean
  color?: string
  showProFeatures?: boolean
  label: "if" | "with" | "when" | "wait" | "then" | "and"
  onDeleteClick: (dataConfirmation: () => Promise<void>) => Promise<void>
  onEditClick: () => Promise<void>
  liveChannelId?: string | undefined
  readOnly: boolean
  service: Service
  stepName: string | undefined
  disabled?: boolean
}

export default function DIYTandaSelected({
  canEdit,
  label,
  onDeleteClick,
  onEditClick,
  liveChannelId,
  readOnly,
  service,
  showProFeatures = false,
  stepName,
  disabled = false,
}: Props) {
  const isFilter = label === "when"
  const isDelay = label === "wait"

  const serviceImage = () => {
    if (isFilter) {
      return <FilterIcon title="filter icon" />
    } else if (isDelay) {
      return <DelayIcon title="delay icon" />
    } else {
      return <img width="20" height="20" src={service.image} alt={`${service.name} icon`} title={service.name} />
    }
  }

  const highlighted = ["if", "then"].includes(label)
  const blockClass = classnames(styles["tanda-selected-block"], {
    [styles.highlighted]: highlighted,
    [styles.clickable]: (canEdit || isFilter) && !disabled,
    [styles.disabled]: disabled,
  })
  const blockStyle = { backgroundColor: disabled ? '#999' : service.brand_color }
  const labelClass = classnames(styles.label, {
    [styles.when]: isFilter || isDelay,
  })

  const dataConfirmation = () => {
    let message: string
    if (label === "if") {
      message = showProFeatures
        ? "If you’ve used any of this trigger’s ingredients in queries, filter code or actions you will have to remove those ingredients before saving this Applet."
        : "If you’ve used any of this trigger’s ingredients in your action you will have to remove those ingredients before saving this Applet."
    } else if (label === "with") {
      message =
        "If you’ve used any of this query’s ingredients in filter code or actions you will have to remove those ingredients before saving this Applet."
    } else if (showProFeatures && ["then", "and"].includes(label)) {
      message = "If you’ve used this action in filter code you will have to remove this code before saving this Applet."
    }

    return new Promise<void>((resolve, reject) => {
      if (message && !window.confirm(message)) {
        reject(cancellationError)
      } else {
        resolve()
      }
    })
  }

  const wrapHandler = (handler: () => Promise<void>) => {
    return (e: MouseEventHandler<HTMLElement>) => {
      e.stopPropagation()
      verifyIntendedPromiseRejection(handler())
    }
  }

  const selectedLiveChannel = service.live_channels?.find(lc => lc.id == liveChannelId)

  return (
    <div className={styles["tanda-selected-container"]}>
      <section
        onClick={(canEdit || isFilter) && !disabled ? wrapHandler(onEditClick) : undefined}
        style={blockStyle}
        className={blockClass}
      >
        <span className={labelClass}>{label}</span>
        {serviceImage()}
        <div className={styles.step}>
          <span className={styles["step-name"]}>{stepName}</span>
          {service.allow_multiple_live_channels && (
            <span className={styles["step-multi-account-selected"]}>{selectedLiveChannel?.user_name_field}</span>
          )}
        </div>
      </section>

      {(canEdit || isFilter || !readOnly) && (
        <div className={classnames(styles["actions-row"], { [styles.highlighted]: highlighted })} style={blockStyle}>
          <div className={styles["actions-overlay"]}>
            <div className={styles.actions}>
              {(canEdit || isFilter) && (
                <button
                  className={classnames(styles.action, { [styles.disabled]: disabled })}
                  onClick={disabled ? undefined : wrapHandler(onEditClick)}
                  disabled={disabled}
                >
                  Edit
                </button>
              )}
              {!readOnly && (
                <button className={styles.action} onClick={wrapHandler(() => onDeleteClick(dataConfirmation))}>
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
